import React, { useMemo, useState } from 'react';
import { members as membersApi, families as familiesApi, batch } from '../../api/endpoints';
import type { BatchOperation } from '../../api/endpoints';
import { Modal, Select, Input, Button } from '../../components/ui';
import useStyles from '../../theme/useStyles';
import { todayISO } from '../../utils/format';
import type { Member, Family } from '../../types';
import { ALIVE, INACTIVE, TRANSFERRED, DECEASED, LIFE_OPTIONS, lifeOf, lifeLabel, isActiveMember } from './lifeStatus';

// Changes a member's life status — Active, Inactive, Transferred, Passed Away —
// in place of deleting them. When the head stops being active while others
// remain, a new head is chosen here and everything is saved together (or sent
// for approval together): the new head, the family's head name, the member.

interface Props {
  member: Member;
  /** The other live members of the family (light rows are fine). */
  family: Member[];
  familyRecord?: Family | null;
  admin: boolean;
  isDarkMode?: boolean;
  /** The full record for a light row (update() replaces the whole record). */
  loadFull: (row: Member) => Promise<Member>;
  onDone: (message: string) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

const isHead = (m?: Member | null) => String(m?.relationToHead || '').trim().toLowerCase() === 'head';
const isSpouse = (m?: Member | null) => /^(spouse|wife|husband)$/i.test(String(m?.relationToHead || '').trim());
const strip = (m: Member) => {
  const { __kind, ...rest } = m as Member & { __kind?: string };
  return rest as Member;
};

const ChangeStatusDialog = ({ member, family, familyRecord, admin, isDarkMode, loadFull, onDone, onError, onCancel }: Props) => {
  const { t } = useStyles(isDarkMode);
  const current = lifeOf(member);
  const choices = LIFE_OPTIONS.filter((o) => o.value !== current);
  const [to, setTo] = useState<string>(choices[0]?.value || ALIVE);
  const [date, setDate] = useState(todayISO());
  const [church, setChurch] = useState('');
  const [reason, setReason] = useState('');
  const [newHeadId, setNewHeadId] = useState('');
  const [widow, setWidow] = useState(true);
  const [saving, setSaving] = useState(false);

  const others = family.filter((m) => m.id !== member.id && isActiveMember(m));
  // Spouse first, then the eldest.
  const candidates = useMemo(() => [...others].sort((a, b) => {
    if (isSpouse(a) !== isSpouse(b)) return isSpouse(a) ? -1 : 1;
    return String(a.dateOfBirth || '9999').localeCompare(String(b.dateOfBirth || '9999'));
  }), [others]);
  const needsNewHead = isHead(member) && isActiveMember(member) && to !== ALIVE && others.length > 0;
  const spouse = others.find((m) => isSpouse(m) || (isHead(m) && isSpouse(member)));
  const offerWidow = to === DECEASED && spouse && /married/i.test(String(spouse.maritalStatus || ''));
  const lockedDeceased = current === DECEASED && !admin;

  const problem = lockedDeceased ? 'Only a System Administrator can change a member who has passed away (to correct a mistake).'
    : !date ? 'Enter the date.'
      : to === TRANSFERRED && !church.trim() ? 'Enter the church they moved to.'
        : needsNewHead && !newHeadId ? 'Choose the new head of the family.'
          : '';

  const save = async () => {
    if (problem) return;
    setSaving(true);
    try {
      const full = strip(await loadFull(member));
      const bodies = new Map<number | string, Member>();
      const order: Array<number | string> = [];
      const put = (m: Member) => { if (!bodies.has(m.id!)) order.push(m.id!); bodies.set(m.id!, m); };
      let headName = '';

      // 1. The new head first, so the family is never without one.
      if (needsNewHead) {
        const row = others.find((m) => String(m.id) === newHeadId)!;
        const head = strip(await loadFull(row));
        headName = head.name || '';
        put({ ...head, relationToHead: 'Head' });
      }
      // 2. A spouse left widowed.
      if (offerWidow && widow && spouse) {
        const existing = bodies.get(spouse.id!) || strip(await loadFull(spouse));
        put({ ...existing, maritalStatus: 'Widowed' });
      }
      // 3. The member.
      const change: Member = {
        ...full,
        status: to,
        statusDate: date,
        statusReason: reason.trim() || undefined,
        dateOfDeath: to === DECEASED ? date : (to === ALIVE ? undefined : full.dateOfDeath),
        transferChurch: to === TRANSFERRED ? church.trim() : (to === ALIVE ? undefined : full.transferChurch),
        ...(needsNewHead ? { relationToHead: 'Other' } : {})
      } as Member;
      put(change);

      const operations: BatchOperation[] = order.map((id) => batch.change(membersApi, id, bodies.get(id)!));
      // 4. The family's header follows the new head.
      if (needsNewHead && familyRecord?.id != null) {
        const { __kind, ...fam } = familyRecord as Family & { __kind?: string };
        const head = bodies.get(Number(newHeadId)) || bodies.get(newHeadId);
        operations.splice(1, 0, batch.change(familiesApi, familyRecord.id, {
          ...fam,
          headOfFamily: headName,
          headGender: head?.gender || fam.headGender,
          contact: head?.mobileNumber || fam.contactNumber || fam.contact,
          contactNumber: head?.mobileNumber || fam.contactNumber || fam.contact
        }));
      }

      const summary = `${member.name}: ${lifeLabel(current)} → ${lifeLabel(to)}`
        + (needsNewHead ? `; ${headName} becomes head of ${member.familyId}` : '');
      await batch.run(operations, summary);
      onDone(`${member.name} is now ${lifeLabel(to)}.${needsNewHead ? ` ${headName} is the new head of the family.` : ''}`);
    } catch (err) {
      onError((err as Error).message || 'Could not change the status.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width="520px"
      title={`Change status — ${member.name}`}
      onClose={saving ? undefined : onCancel}
      isDarkMode={isDarkMode}
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button write onClick={save} disabled={saving || Boolean(problem)}>{saving ? 'Saving…' : 'Save'}</Button>
        </>
      )}
    >
      <p style={{ margin: '0 0 12px', color: t.textMuted, fontSize: t.fontSize.md }}>
        Now: <strong style={{ color: t.textMain }}>{lifeLabel(current)}</strong>.
        {' '}Nobody is deleted — the record and its history stay.
      </p>

      {lockedDeceased ? (
        <p style={{ color: t.danger, fontSize: t.fontSize.sm }}>{problem}</p>
      ) : (
        <>
          <Select label="New status" placeholder="" options={choices} value={to} onChange={(e) => setTo(e.target.value)} />
          <Input
            label={to === DECEASED ? 'Date of passing' : to === TRANSFERRED ? 'Date of transfer' : 'From date'}
            type="date" required value={date} onChange={(e) => setDate(e.target.value)}
          />
          {to === TRANSFERRED && (
            <Input label="Moved to (church)" required placeholder="Name of the church they joined"
              value={church} onChange={(e) => setChurch(e.target.value)} />
          )}
          <Input
            label={to === INACTIVE ? 'Reason' : 'Note (optional)'}
            placeholder={to === INACTIVE ? 'e.g. Stopped attending, moved away for work'
              : to === ALIVE ? 'e.g. Returned to the church' : ''}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />

          {needsNewHead && (
            <div style={{ marginTop: '6px', padding: '10px 12px', borderRadius: t.radius.md, border: `1px solid ${t.border}` }}>
              <p style={{ margin: '0 0 8px', color: t.textMain, fontSize: t.fontSize.sm }}>
                {member.name} is the <strong>head</strong> of {member.familyId}. Choose who becomes the new head —
                the family name and contact follow them.
              </p>
              <Select
                label="New head of the family" placeholder="Choose a member..."
                options={candidates.map((m) => ({
                  value: String(m.id),
                  label: `${m.name}${m.relationToHead ? ` (${m.relationToHead})` : ''}${m.memberId ? ` — ${m.memberId}` : ''}`
                }))}
                value={newHeadId} onChange={(e) => setNewHeadId(e.target.value)}
              />
            </div>
          )}
          {isHead(member) && to !== ALIVE && others.length === 0 && (
            <p style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>
              {member.name} is the last active member, so the family becomes {to === DECEASED ? 'Closed' : lifeLabel(to)} too.
            </p>
          )}
          {offerWidow && spouse && (
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', color: t.textMain, fontSize: t.fontSize.sm, cursor: 'pointer' }}>
              <input type="checkbox" checked={widow} onChange={(e) => setWidow(e.target.checked)} />
              Mark {spouse.name} as Widowed
            </label>
          )}
          {problem && !lockedDeceased && (
            <p style={{ color: t.textFaint, fontSize: t.fontSize.xs, marginTop: '8px' }}>{problem}</p>
          )}
        </>
      )}
    </Modal>
  );
};

export default ChangeStatusDialog;
